import { z } from "zod";



import { FormField } from "../../../FormEngine/types";
import { multiSelectResponseSchema } from "../../common/schemaHelper";





/**
 * Other Weight Management History form schema
 *
 * This schema defines the fields for the Other Weight Management History form
 * using the FormEngine component.
 */
export const otherWeightManagementHistorySchema: FormField[] = [
  {
    type: "multi-select",
    name: "medicalHistory",
    title: "Medical History",
    description: "Do you or your family have a history of any of the following?",
    options: [
      "Medullary Thyroid Carcinoma (not Papillary or Follicular)",
      "MEN II Syndrome (Multiple Endocrine Neoplasia Syndrome Type 2)",
    ].map((option) => ({ label: option, value: option })),
    fieldProps: {
      includeNoneOption: true,
    },
    rule: z.array(z.string()).min(1, { message: "Please select at least one option" }),
  },
  {
    type: "radio",
    name: "type2Diabetes",
    title: "Type 2 Diabetes",
    description: "Do you have Type 2 Diabetes?",
    fieldProps: {
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
      sx: { flexDirection: "row", gap: 1 },
    },
    rule: z.boolean(),
  },
  {
    type: "multi-select-response",
    name: "diabetesMedications",
    title: "Diabetes Medications",
    description: "Do you take any of the following diabetes medications?",
    options: [
      "Insulin",
      "Metformin",
      "Sulfonylureas (e.g. Glipizide, Glimepiride, Glyburide)",
      "Other",
    ].map((option) => ({
      label: option,
      value: option,
    })),
    schema: [
      {
        type: "input",
        name: "description",
        label: "Please describe",
      },
    ],
    fieldProps: {
      includeNoneOption: true,
    },
    rule: z
      .array(multiSelectResponseSchema)
      .min(1, { message: "Please select at least one option" }),
    conditional: {
      label: "type2Diabetes",
      values: [true],
      resetOnHide: true,
    },
  },
  {
    type: "radio",
    name: "checkBloodSugar",
    title: "Check Blood Sugar",
    description: "Do you check your blood sugar?",
    fieldProps: {
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
      sx: { flexDirection: "row", gap: 1 },
    },
    rule: z.boolean().nullable().refine((data) => data !== null, {
      message: "Please select an option",
    }),
    conditional: {
      label: "type2Diabetes",
      values: [true],
      resetOnHide: true,
    },
  },
  {
    type: "group-input",
    name: "eyeDisease",
    title: "Eye Disease",
    description:
      "Have you been diagnosed with eye disease related to diabetes such as retinopathy?",
    group: [
      {
        type: "radio",
        name: "eyeDisease.response",
        fieldProps: {
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
          sx: { flexDirection: "row", gap: 1 },
        },
      },
      {
        type: "input",
        name: "eyeDisease.description",
        label: "Description",
        conditional: {
          label: "eyeDisease.response",
          values: [true],
        },
      },
    ],
    fieldProps: {
      direction: "column",
    },
    conditional: {
      label: "type2Diabetes",
      values: [true],
      resetOnHide: true,
    },
  },
  {
    type: "multi-select",
    name: "gastrointestinalProblems",
    title: "Gastrointestinal Problems",
    description: "Have you had any of the following gastrointestinal problems?",
    options: [
      "Crohns' disease",
      "Ulcerative Colitis",
      "Gastroparesis",
      "Celiac Disease",
      "Bowel Paralysis",
      "Gallstones",
      "Pancreatitis",
      "Pancreatic Cancer",
    ].map((option) => ({ label: option, value: option })),
    fieldProps: {
      includeOtherOption: true,
      includeNoneOption: true,
    },
    rule: z.array(z.string()).min(1, { message: "Please select at least one option" }),
  },
  {
    type: "group-input",
    name: "stomachOrBowelSurgery",
    title: "Stomach or Bowel Surgery",
    description: "Have you had any stomach or bowel surgery besides weight loss surgery?",
    group: [
      {
        type: "radio",
        name: "stomachOrBowelSurgery.response",
        fieldProps: {
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
          sx: { flexDirection: "row", gap: 1 },
        },
      },
      {
        type: "input",
        name: "stomachOrBowelSurgery.description",
        label: "Description",
        conditional: {
          label: "stomachOrBowelSurgery.response",
          values: [true],
        },
      },
    ],
    fieldProps: {
      direction: "column",
    },
  },
  {
    type: "group-input",
    name: "upcomingSurgeries",
    title: "Upcoming Surgeries",
    description: "Are you planning any procedures or surgeries (of any kind) within the next 3 months?",
    group: [
      {
        type: "radio",
        name: "upcomingSurgeries.response",
        fieldProps: {
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
          sx: { flexDirection: "row", gap: 1 },
        },
      },
      {
        type: "input",
        name: "upcomingSurgeries.description",
        label: "Description",
        conditional: {
          label: "upcomingSurgeries.response",
          values: [true],
        },
      },
    ],
    fieldProps: {
      direction: "column",
    },
  },
  {
    type: "multi-select",
    name: "femaleHistory",
    title: "For Women:",
    description: "Have you had or are you experiencing any of the following?",
    options: [
      "Infertility",
      "PCOS",
      "Currently Pregnant",
      "Trying to become pregnant within the next 6 months",
      "Currently Breastfeeding",
      "Currently on Birth Control",
    ].map((option) => ({ label: option, value: option })),
    fieldProps: {
      includeOtherOption: true,
      includeNoneOption: true,
      noneOptionLabel: "None/Not Applicable",
    },
    rule: z.array(z.string()).min(1, { message: "Please select at least one option" }),
  },
];

export default otherWeightManagementHistorySchema;